import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import icon from 'flarum/common/helpers/icon';
import extractText from 'flarum/common/utils/extractText';
import QrCode from './QrCode';
import { rewardStages } from '../rewardDisplay.mjs';
import { buildShareText } from '../shareText.mjs';
import {
  clearReferral,
  loadReferral,
  referralCodeFromUrl,
  saveReferral,
  withoutReferralParameter,
} from '../referralStorage.mjs';

export default class CampaignPage extends Page {
  bodyClass = 'ReferralCampaignPage';

  oninit(vnode) {
    super.oninit(vnode);

    this.slug = m.route.param('slug');
    this.loading = true;
    this.creatingCode = false;
    this.error = null;
    this.invalidReferral = false;
    this.payload = null;
    this.load();
  }

  view() {
    if (this.loading) {
      return <LoadingIndicator className="ReferralCampaign-loading" size="large" />;
    }

    if (this.error || !this.payload) {
      return (
        <main className="ReferralCampaign-error container">
          <div className="ReferralCampaign-errorPanel">
            {icon('fas fa-exclamation-circle')}
            <h1>{app.translator.trans('dzpbw-referral-campaigns.forum.error.title')}</h1>
            <p>{app.translator.trans('dzpbw-referral-campaigns.forum.error.description')}</p>
            <Button className="Button Button--primary" icon="fas fa-redo" onclick={() => this.load()}>
              {app.translator.trans('dzpbw-referral-campaigns.forum.error.retry')}
            </Button>
          </div>
        </main>
      );
    }

    const { campaign, viewer, referralValid } = this.payload;

    return (
      <main className="ReferralCampaign">
        {this.hero(campaign, viewer, referralValid)}
        {this.invalidReferral && (
          <div className="ReferralCampaign-notice ReferralCampaign-notice--warning" role="status">
            <div className="container">
              {icon('fas fa-exclamation-triangle')}
              <span>{app.translator.trans('dzpbw-referral-campaigns.forum.referral.invalid')}</span>
            </div>
          </div>
        )}
        {referralValid && !viewer.isAuthenticated && (
          <div className="ReferralCampaign-notice ReferralCampaign-notice--success" role="status">
            <div className="container">
              {icon('fas fa-check-circle')}
              <span>{app.translator.trans('dzpbw-referral-campaigns.forum.referral.accepted')}</span>
            </div>
          </div>
        )}
        <div className="ReferralCampaign-body container">
          {viewer.isAuthenticated && viewer.canInvite && this.sharePanel(campaign, viewer)}
          {viewer.isAuthenticated && !viewer.canInvite && this.ineligiblePanel(campaign, viewer)}
          {!viewer.isAuthenticated && this.guestPanel(campaign, referralValid)}
          {viewer.isAuthenticated && viewer.stats && this.statsPanel(campaign, viewer)}
          {viewer.isAuthenticated && viewer.invitations && this.invitationsPanel(viewer)}
          {this.rulesPanel(campaign)}
        </div>
      </main>
    );
  }

  hero(campaign, viewer, referralValid) {
    const heroImage = `${app.forum.attribute('assetsBaseUrl')}/extensions/dzpbw-referral-campaigns/referral-campaign-hero.png`;
    const action = this.heroAction(campaign, viewer, referralValid);

    return (
      <section className="ReferralCampaign-hero">
        <div className="ReferralCampaign-heroInner container">
          <div className="ReferralCampaign-heroCopy">
            <Link className="ReferralCampaign-back" href={app.route('index')}>
              {icon('fas fa-arrow-left')}
              <span>{app.translator.trans('dzpbw-referral-campaigns.forum.hero.back')}</span>
            </Link>
            <span className={`ReferralCampaign-status ReferralCampaign-status--${campaign.status}`}>
              {this.statusLabel(campaign.status)}
            </span>
            <h1>{campaign.title}</h1>
            <p>{campaign.description}</p>
            <div className="ReferralCampaign-rewardStages">
              {rewardStages(campaign).map((stage) => (
                <div className="ReferralCampaign-rewardStage">
                  <span className="ReferralCampaign-rewardStageLabel">
                    {app.translator.trans(`dzpbw-referral-campaigns.forum.hero.${stage.key}_stage`)}
                  </span>
                  <span className="ReferralCampaign-rewardValue">
                    <strong>{this.points(stage.inviterMinor)}</strong>
                    <small>{app.translator.trans('dzpbw-referral-campaigns.forum.hero.inviter_reward')}</small>
                  </span>
                  <i aria-hidden="true" />
                  <span className="ReferralCampaign-rewardValue">
                    <strong>{this.points(stage.inviteeMinor)}</strong>
                    <small>{app.translator.trans('dzpbw-referral-campaigns.forum.hero.invitee_reward')}</small>
                  </span>
                </div>
              ))}
            </div>
            {action}
          </div>
          <img className="ReferralCampaign-heroImage" src={heroImage} alt="" aria-hidden="true" />
        </div>
      </section>
    );
  }

  heroAction(campaign, viewer, referralValid) {
    if (campaign.status !== 'active') {
      return (
        <Button className="Button ReferralCampaign-heroButton" disabled>
          {this.statusLabel(campaign.status)}
        </Button>
      );
    }

    if (!viewer.isAuthenticated) {
      return (
        <Button
          className="Button ReferralCampaign-heroButton"
          icon={referralValid ? 'fas fa-user-plus' : 'fas fa-arrow-right'}
          onclick={() => app.modal.show(SignUpModal)}
        >
          {app.translator.trans(
            referralValid
              ? 'dzpbw-referral-campaigns.forum.hero.join_with_invite'
              : 'dzpbw-referral-campaigns.forum.hero.join'
          )}
        </Button>
      );
    }

    if (!viewer.canInvite) return null;

    return (
      <Button
        className="Button ReferralCampaign-heroButton"
        icon="fas fa-paper-plane"
        onclick={() => {
          document.getElementById('referral-share')?.scrollIntoView({ behavior: 'smooth' });
        }}
      >
        {app.translator.trans('dzpbw-referral-campaigns.forum.hero.invite_now')}
      </Button>
    );
  }

  sharePanel(campaign, viewer) {
    if (!viewer.inviteCode || !viewer.shareUrl) {
      return (
        <section id="referral-share" className="ReferralCampaign-section ReferralCampaign-createCode">
          <div>
            <span className="ReferralCampaign-sectionIcon">{icon('fas fa-ticket-alt')}</span>
            <h2>{app.translator.trans('dzpbw-referral-campaigns.forum.share.create_title')}</h2>
            <p>{app.translator.trans('dzpbw-referral-campaigns.forum.share.create_description')}</p>
          </div>
          <Button
            className="Button Button--primary"
            icon="fas fa-plus"
            loading={this.creatingCode}
            onclick={() => this.createInviteCode()}
          >
            {app.translator.trans('dzpbw-referral-campaigns.forum.share.create_button')}
          </Button>
        </section>
      );
    }

    const shareText = buildShareText({
      shareMessage: campaign.shareMessage,
      defaultMessage: extractText(app.translator.trans('dzpbw-referral-campaigns.forum.share.description')),
      codeLine: extractText(app.translator.trans('dzpbw-referral-campaigns.forum.share.code_line', { code: viewer.inviteCode })),
      linkLine: extractText(app.translator.trans('dzpbw-referral-campaigns.forum.share.link_line', { url: viewer.shareUrl })),
    });

    return (
      <section id="referral-share" className="ReferralCampaign-section ReferralCampaign-share">
        <div className="ReferralCampaign-shareMain">
          <span className="ReferralCampaign-kicker">
            {app.translator.trans('dzpbw-referral-campaigns.forum.share.kicker')}
          </span>
          <h2>{app.translator.trans('dzpbw-referral-campaigns.forum.share.title')}</h2>
          <p>{campaign.shareMessage || app.translator.trans('dzpbw-referral-campaigns.forum.share.description')}</p>
          <label>{app.translator.trans('dzpbw-referral-campaigns.forum.share.code_label')}</label>
          <div className="ReferralCampaign-codeRow">
            <code>{viewer.inviteCode}</code>
            <Button
              className="Button Button--icon"
              icon="far fa-copy"
              aria-label={app.translator.trans('dzpbw-referral-campaigns.forum.share.copy_code')}
              title={app.translator.trans('dzpbw-referral-campaigns.forum.share.copy_code')}
              onclick={() => this.copy(shareText)}
            />
          </div>
          <label>{app.translator.trans('dzpbw-referral-campaigns.forum.share.link_label')}</label>
          <div className="ReferralCampaign-linkRow">
            <input className="FormControl" readonly value={viewer.shareUrl} onclick={(event) => event.target.select()} />
            <Button
              className="Button Button--primary"
              icon="fas fa-link"
              onclick={() => this.copy(shareText)}
            >
              {app.translator.trans('dzpbw-referral-campaigns.forum.share.copy_link')}
            </Button>
            {typeof navigator.share === 'function' && (
              <Button
                className="Button Button--icon"
                icon="fas fa-share-alt"
                aria-label={app.translator.trans('dzpbw-referral-campaigns.forum.share.system_share')}
                title={app.translator.trans('dzpbw-referral-campaigns.forum.share.system_share')}
                onclick={() => this.systemShare(campaign, viewer.shareUrl)}
              />
            )}
          </div>
        </div>
        <div className="ReferralCampaign-qrPanel">
          <QrCode
            value={viewer.shareUrl}
            label={app.translator.trans('dzpbw-referral-campaigns.forum.share.qr_label')}
          />
          <span>{app.translator.trans('dzpbw-referral-campaigns.forum.share.qr_caption')}</span>
        </div>
      </section>
    );
  }

  ineligiblePanel(campaign, viewer) {
    return (
      <section className="ReferralCampaign-section ReferralCampaign-ineligible">
        <span className="ReferralCampaign-sectionIcon">{icon('fas fa-lock')}</span>
        <div>
          <h2>{app.translator.trans('dzpbw-referral-campaigns.forum.eligibility.title')}</h2>
          <p>{app.translator.trans('dzpbw-referral-campaigns.forum.eligibility.description')}</p>
          {viewer.receivedInvitation && (
            <span className={`ReferralCampaign-state ReferralCampaign-state--${viewer.receivedInvitation.status}`}>
              {app.translator.trans('dzpbw-referral-campaigns.forum.eligibility.own_status', {
                status: this.attributionStatus(viewer.receivedInvitation.status),
              })}
            </span>
          )}
        </div>
      </section>
    );
  }

  guestPanel(campaign, referralValid) {
    return (
      <section className="ReferralCampaign-section ReferralCampaign-guest">
        <span className="ReferralCampaign-sectionIcon">
          {icon(referralValid ? 'fas fa-envelope-open-text' : 'fas fa-user-friends')}
        </span>
        <div>
          <h2>
            {app.translator.trans(
              referralValid
                ? 'dzpbw-referral-campaigns.forum.guest.invited_title'
                : 'dzpbw-referral-campaigns.forum.guest.title'
            )}
          </h2>
          <p>
            {app.translator.trans('dzpbw-referral-campaigns.forum.guest.description', {
              days: campaign.qualificationDays,
            })}
          </p>
        </div>
        <Button className="Button Button--primary" icon="fas fa-user-plus" onclick={() => app.modal.show(SignUpModal)}>
          {app.translator.trans('dzpbw-referral-campaigns.forum.guest.register')}
        </Button>
      </section>
    );
  }

  statsPanel(campaign, viewer) {
    const qualified = viewer.stats.qualified || 0;
    const nextTier = viewer.nextTier;
    const progress = nextTier ? Math.min(100, Math.round((qualified / nextTier.threshold) * 100)) : 100;

    return (
      <section className="ReferralCampaign-section ReferralCampaign-progress">
        <div className="ReferralCampaign-sectionHeading">
          <span className="ReferralCampaign-kicker">
            {app.translator.trans('dzpbw-referral-campaigns.forum.progress.kicker')}
          </span>
          <h2>{app.translator.trans('dzpbw-referral-campaigns.forum.progress.title')}</h2>
        </div>
        <div className="ReferralCampaign-statGrid">
          {this.stat('fas fa-hourglass-half', viewer.stats.pending, 'pending')}
          {this.stat('fas fa-check', viewer.stats.qualified, 'qualified')}
          {this.stat('fas fa-coins', this.points(viewer.stats.earnedMinor), 'earned')}
        </div>
        <div className="ReferralCampaign-progressTrack" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
        <p className="ReferralCampaign-progressText">
          {nextTier
            ? app.translator.trans('dzpbw-referral-campaigns.forum.progress.next', {
                remaining: Math.max(0, nextTier.threshold - qualified),
                bonus: this.points(nextTier.bonusMinor),
              })
            : app.translator.trans('dzpbw-referral-campaigns.forum.progress.complete')}
        </p>
        {campaign.tiers.length > 0 && (
          <div className="ReferralCampaign-tierList">
            {campaign.tiers.map((tier) => (
              <div className={qualified >= tier.threshold ? 'is-complete' : ''}>
                <span>{icon(qualified >= tier.threshold ? 'fas fa-check-circle' : 'far fa-circle')}</span>
                <strong>{tier.threshold}</strong>
                <small>
                  {app.translator.trans('dzpbw-referral-campaigns.forum.progress.tier_bonus', {
                    bonus: this.points(tier.bonusMinor),
                  })}
                </small>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  stat(iconName, value, key) {
    return (
      <div className="ReferralCampaign-stat">
        <span>{icon(iconName)}</span>
        <strong>{value}</strong>
        <small>{app.translator.trans(`dzpbw-referral-campaigns.forum.progress.${key}`)}</small>
      </div>
    );
  }

  invitationsPanel(viewer) {
    return (
      <section className="ReferralCampaign-section ReferralCampaign-invitations">
        <div className="ReferralCampaign-sectionHeading">
          <span className="ReferralCampaign-kicker">
            {app.translator.trans('dzpbw-referral-campaigns.forum.invitations.kicker')}
          </span>
          <h2>{app.translator.trans('dzpbw-referral-campaigns.forum.invitations.title')}</h2>
        </div>
        {viewer.invitations.length === 0 ? (
          <div className="ReferralCampaign-empty">
            {icon('far fa-paper-plane')}
            <p>{app.translator.trans('dzpbw-referral-campaigns.forum.invitations.empty')}</p>
          </div>
        ) : (
          <div className="ReferralCampaign-invitationList">
            {viewer.invitations.map((invitation) => (
              <div className="ReferralCampaign-invitation">
                <span className="ReferralCampaign-avatar">{invitation.displayName.slice(-2)}</span>
                <div>
                  <strong>{invitation.displayName}</strong>
                  <small>{this.date(invitation.registeredAt)}</small>
                </div>
                <span className={`ReferralCampaign-state ReferralCampaign-state--${invitation.status}`}>
                  {this.attributionStatus(invitation.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  rulesPanel(campaign) {
    return (
      <section className="ReferralCampaign-section ReferralCampaign-rules">
        <div className="ReferralCampaign-sectionHeading">
          <span className="ReferralCampaign-kicker">
            {app.translator.trans('dzpbw-referral-campaigns.forum.rules.kicker')}
          </span>
          <h2>{app.translator.trans('dzpbw-referral-campaigns.forum.rules.title')}</h2>
        </div>
        <ol>
          <li>
            <strong>1</strong>
            <span>{app.translator.trans('dzpbw-referral-campaigns.forum.rules.signup')}</span>
          </li>
          <li>
            <strong>2</strong>
            <span>{app.translator.trans('dzpbw-referral-campaigns.forum.rules.email')}</span>
          </li>
          <li>
            <strong>3</strong>
            <span>
              {app.translator.trans('dzpbw-referral-campaigns.forum.rules.wait', {
                days: campaign.qualificationDays,
              })}
            </span>
          </li>
          <li>
            <strong>4</strong>
            <span>{app.translator.trans('dzpbw-referral-campaigns.forum.rules.certification')}</span>
          </li>
        </ol>
        {campaign.inviterRewardCapMinor !== null && (
          <p className="ReferralCampaign-cap">
            {icon('fas fa-info-circle')}
            {app.translator.trans('dzpbw-referral-campaigns.forum.rules.cap', {
              points: this.points(campaign.inviterRewardCapMinor),
            })}
          </p>
        )}
        {campaign.termsUrl && (
          <a className="ReferralCampaign-terms" href={campaign.termsUrl} target="_blank" rel="noopener noreferrer">
            {app.translator.trans('dzpbw-referral-campaigns.forum.rules.terms')}
            {icon('fas fa-external-link-alt')}
          </a>
        )}
      </section>
    );
  }

  load() {
    this.loading = true;
    this.error = null;
    const urlHasReferral = new URL(window.location.href).searchParams.has('ref');
    const urlReferral = referralCodeFromUrl(window.location.href);
    const stored = loadReferral(window.localStorage);
    const storedReferral = stored?.campaignSlug === this.slug ? stored.code : null;
    const referralCode = urlReferral || storedReferral;
    const query = referralCode ? `?ref=${encodeURIComponent(referralCode)}` : '';

    if (urlHasReferral && urlReferral === null) {
      this.invalidReferral = true;
      clearReferral(window.localStorage);
      window.history.replaceState({}, '', withoutReferralParameter(window.location.href));
    }

    app.request({
      method: 'GET',
      url: `${app.forum.attribute('apiUrl')}/dzpbw/referral-campaigns/${encodeURIComponent(this.slug)}${query}`,
    }).then(
      (payload) => {
        this.payload = payload;
        this.loading = false;

        if (urlHasReferral && urlReferral !== null) {
          if (payload.referralValid) {
            saveReferral(window.localStorage, this.slug, urlReferral);
          } else {
            this.invalidReferral = true;
            clearReferral(window.localStorage);
          }

          window.history.replaceState({}, '', withoutReferralParameter(window.location.href));
        } else if (storedReferral && !payload.referralValid) {
          clearReferral(window.localStorage);
        }

        m.redraw();
      },
      (error) => {
        this.error = error;
        this.loading = false;
        m.redraw();
      }
    );
  }

  createInviteCode() {
    this.creatingCode = true;
    app.request({
      method: 'POST',
      url: `${app.forum.attribute('apiUrl')}/dzpbw/referral-campaigns/${encodeURIComponent(this.slug)}/invite-code`,
    }).then(
      () => {
        this.creatingCode = false;
        this.load();
      },
      (error) => {
        this.creatingCode = false;
        app.alerts.show({ type: 'error' }, error.message || app.translator.trans('core.lib.error.generic_message'));
        m.redraw();
      }
    );
  }

  copy(value) {
    const fallback = () => {
      const input = document.createElement('textarea');
      input.value = value;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    };
    const result = navigator.clipboard?.writeText
      ? navigator.clipboard.writeText(value).catch(fallback)
      : Promise.resolve(fallback());

    return Promise.resolve(result).then(() => {
      app.alerts.show(
        { type: 'success' },
        app.translator.trans('dzpbw-referral-campaigns.forum.share.copied')
      );
    });
  }

  systemShare(campaign, url) {
    navigator.share({
      title: campaign.title,
      text: campaign.shareMessage || campaign.description,
      url,
    }).catch(() => {});
  }

  points(minor) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format((minor || 0) / 10000);
  }

  date(value) {
    if (!value) return '';

    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
  }

  statusLabel(status) {
    return app.translator.trans(`dzpbw-referral-campaigns.forum.status.${status}`);
  }

  attributionStatus(status) {
    return app.translator.trans(`dzpbw-referral-campaigns.forum.attribution_status.${status}`);
  }
}
